<style scoped>
.line-separator {
    content: '';
    @apply block my-2;
    @apply border-b border-dotted;
}

.slide-left-enter-active,
.slide-left-leave-active {
    transition: all 300ms cubic-bezier(1, 0, 0, 1);
}
.slide-left-enter {
    transform: translateX(30px);
    opacity: 0;
}
.slide-left-leave-to {
    transform: translateX(-30px);
    opacity: 0;
}

.slide-right-enter-active,
.slide-right-leave-active {
    transition: all 300ms cubic-bezier(1, 0, 0, 1);
}
.slide-right-enter {
    transform: translateX(-30px);
    opacity: 0;
}
.slide-right-leave-to {
    transform: translateX(30px);
    opacity: 0;
}

.reminder-message {
    background: rgba(255, 242, 204, 1);
    @apply border border-primary m-3 px-8 py-2 font-semibold;

    & ul {
        counter-reset: item;
        @apply font-bold list-outside list-disc;
    }

    & a {
        @apply text-blue underline;
    }
}
</style>

<template>
    <div class="mt-4 px-2 md:pl-16 md:pr-4">
        <div v-if="loading" class="flex justify-center m-3 w-full">
            <spinner />
        </div>
        <template v-else>
            <transition :name="transitionName" mode="out-in">
                <productInfo
                    v-if="!showConfirmPage"
                    :product="product"
                    :productLinks="productLinks"
                    :showProdDespImg="true"
                >
                    <div
                        class="text-start w-full text-red text-sm sm:mr-3 my-2"
                        v-if="maxCustOrdQty"
                    >
                        {{
                            maxCustOrdQty >= productActualRemainingQty
                                ? $t(
                                      'product.qty.let',
                                      productActualRemainingQty
                                  )
                                : $t('product.max.qty.long', maxCustOrdQty)
                        }}
                    </div>
                    <div class="line-separator" />
                    <div
                        class="flex text-sm text-gray-650 font-extrabold leading-none mt-4 mb-2"
                        v-if="product.prodRefPrcAmt"
                    >
                        <div class="w-20 whitespace-no-wrap">
                            {{
                                $t(
                                    'product.ref.prc.amt.' +
                                        product.prodTypeCode.toLowerCase()
                                )
                            }}
                            :
                        </div>
                        <div>{{ product.prodRefPrcAmt | price }}</div>
                    </div>
                    <div class="flex text-lg leading-none mb-4">
                        <div
                            class="w-20 whitespace-no-wrap font-extrabold"
                            :class="{
                                'text-red': isLessThanOrigPrcAmt,
                                'text-darkGrayishBlue': !isLessThanOrigPrcAmt
                            }"
                        >
                            {{ $t('product.price.member') }} :
                        </div>
                        <div
                            class="flex flex-grow items-center font-extrabold"
                            :class="{
                                'text-red': isLessThanOrigPrcAmt,
                                'text-darkGrayishBlue': !isLessThanOrigPrcAmt
                            }"
                        >
                            <div
                                class="font-extrabold"
                                :class="{
                                    'text-red': product.prodUnitPrcAmt === 0
                                }"
                            >
                                {{ product.prodUnitPrcAmt | price(true) }}
                            </div>
                            <div
                                class="font-extrabold text-gray-300 line-through text-base ml-6"
                                v-if="isLessThanOrigPrcAmt"
                            >
                                {{ product.prodOrigUnitPrcAmt | price }}
                            </div>
                        </div>
                    </div>
                    <div
                        class="flex items-center text-lg font-semibold leading-none"
                    >
                        <div class="w-20 whitespace-no-wrap">
                            {{ $t('product.qty') }} :
                        </div>
                        <div class="flex-1">
                            <quantityInput
                                v-model="product.prodOrdQty"
                                :max="maxQty"
                                class="w-full bg-white"
                            />
                        </div>
                    </div>
                    <div class="w-full mt-2">
                        <div class="flex flex-wrap items-center">
                            <button
                                class="btn p-0 relative max-w-full text-sm focus:outline-none"
                                @click="switchToConfirmPage"
                            >
                                <div
                                    class="flex flex-row items-center justify-center w-full bg-secondary py-1 px-2 lg:px-3 text-white"
                                >
                                    <div class="icon ">
                                        <shoppingCartSvg />
                                    </div>

                                    <div class="pl-1 lg:pl-2 text-center">
                                        {{ $t('cart.add') }}
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                </productInfo>
                <div
                    v-if="!!showConfirmPage"
                    class="flex flex-col md:flex-row md:flex-wrap mt-4"
                >
                    <div class="w-full md:w-2/3 border border-primary">
                        <div class="w-full text-center text-lg font-bold my-4">
                            {{ $t('cart.greet') }}
                        </div>
                        <div
                            class="w-full flex flex-col items-center text-red"
                            v-if="!cartCanProcess"
                        >
                            <div class="text-base font-bold">
                                {{ $t('cart.error.some.products.excess.qty') }}
                            </div>
                        </div>
                        <productTable
                            :list="[product]"
                            :dsctAmt="
                                cpnDsctAmt + adhocDsctAmt + crdtPntDsctAmt
                            "
                            :crdtPntEarnAmt="crdtPntEarn"
                            :showCrdtPntEarnAmt="false"
                            @deleteProduct="switchToProductInfoPage"
                            :showSummary="false"
                            :maxQty="() => maxQty"
                            class="product-table w-full mt-4"
                        />
                    </div>
                    <div
                        class="w-full md:flex md:flex-col md:w-1/3 md:pl-4"
                        ref="dlvrySiteAndPaymentSelection"
                    >
                        <div
                            class="flex flex-col items-center border border-primary mt-4 md:mt-0 py-4 text-lg"
                        >
                            <div class="font-bold">請選擇取貨地點</div>
                            <div
                                class="w-full flex flex-row items-start mt-2 px-8"
                            >
                                <div class="w-1/3">取貨地點:</div>
                                <deliveryAddressSelection
                                    class="w-2/3"
                                    v-model.number="dlvryCmpySiteKey"
                                    :disabled="isFriendCustomer"
                                    :showDlvryCmpySiteAddr="
                                        showDlvryCmpySiteAddr
                                    "
                                />
                            </div>
                            <div
                                v-if="isFriendCustomer"
                                class="mt-2 text-xs text-blue"
                            >
                                (取貨地點只能由相關的企業會員更改)
                            </div>
                        </div>
                        <div
                            v-if="needPayment"
                            class="flex flex-col flex-grow items-center border border-primary mt-4 py-4 px-2 text-lg"
                        >
                            <div class="font-bold">
                                請選擇付款方式
                            </div>
                            <paymentSelection
                                ref="paymentSelection"
                                class="flex-grow"
                                :disabledProviders="['CASH']"
                                @paymentSelected="v => (paymentOption = v)"
                            />
                        </div>
                    </div>
                    <div
                        class="w-full py-4 flex flex-col md:w-2/3 border border-t-0 border-primary"
                    >
                        <div
                            class="ml-auto mr-4 flex flex-col items-center mt-4"
                        >
                            <loadingButton
                                class="btn btn-primary self-end py-2 font-bold mt-1 px-4"
                                :class="{
                                    'opacity-50': !cartCanProcess
                                }"
                                :disabled="!cartCanProcess"
                                :status="status"
                                @click="requestConfirmOrder"
                            >
                                {{ $t('order.confirm') }}
                            </loadingButton>
                            <errorBox
                                v-if="errors && errors.server"
                                :message="errors.server"
                            />
                            <confirmOrderModal
                                v-if="showConfirmOrder"
                                :paymentOption="paymentOption"
                                :needPayment="needPayment"
                                @confirm="confirmOrder"
                                @cancel="showConfirmOrder = false"
                            />
                        </div>
                        <div class="reminder-message">
                            <span>請注意： </span>
                            <ul>
                                <li>
                                    此會員尊享優惠不可與T-Coins
                                    或其他優惠及推廣同時使用
                                </li>
                                <li>
                                    按下確認付款後將會轉跳到您所選擇的付款介面
                                </li>
                                <li>
                                    我們將發送電子收據至您的電郵地址。請把我們的系統電郵地址
                                    <a href="mailto:cs@tryhala.com">
                                        cs@tryhala.com
                                    </a>
                                    加入為聯絡人，以免您的電郵系統誤當為垃圾郵件
                                </li>
                                <li>
                                    若有任何查詢，請電郵至
                                    <a href="mailto:cs@tryhala.com">
                                        cs@tryhala.com
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </transition>
        </template>

        <modal type="custom" v-if="orderStatusModalMsg">
            {{ orderStatusModalMsg }}
        </modal>
        <modal
            v-if="showDeliverySiteOrPaymentNotSelectedModal"
            type="alert"
            :showCancel="false"
            @confirm="showDeliverySiteOrPaymentNotSelectedModal = false"
            @backdrop="showDeliverySiteOrPaymentNotSelectedModal = false"
            :confirmText="$t('common.confirm')"
        >
            <div class="w-full text-center text-xl font-black">
                {{ '請選擇付款方式 及 取貨地點' }}
            </div>
        </modal>
        <accountInfoFormModal
            v-if="showInfoModal"
            @success="infoSaveSuccess"
            @hide="showInfoModal = false"
        />
        <askLoginRegisterModal
            :showModal="showAskLoginRegisterModal"
            @hide="showAskLoginRegisterModal = false"
        >
            <template #buttons>
                <div
                    class="flex flex-col items-center font-bold text-lg sm:text-xl"
                >
                    <div>
                        想享受「試吓啦」會員尊屬優惠？
                    </div>
                    <div>
                        即刻
                        <span class="text-primary">免費</span>
                        加入「試吓啦」享受更多禮遇！
                    </div>
                </div>
                <div class="flex flex-col self-center">
                    <router-link
                        tag="button"
                        class="btn btn-white py-3 text-xl flex justify-center mt-2"
                        :to="{ name: 'login', params: { lastRoute: $route } }"
                    >
                        {{ '「試吓啦」會員登入' }}
                    </router-link>
                    <a
                        href="https://tryhala.com/info/member_enquiry/"
                        target="_blank"
                        class="btn btn-primary py-3 text-xl flex justify-center mt-2"
                    >
                        {{ '我想成為「試吓啦」會員' }}
                    </a>
                </div>
            </template>
        </askLoginRegisterModal>
    </div>
</template>

<script>
import productInfo from '../../components/productInfo.vue';
import price from '../../components/priceFilter';
import quantityInput from '../../components/quantityInput.vue';
import shoppingCartSvg from '../../assets/svg/shopping_cart.svg';
import productTable from '../../components/productTable.vue';
import deliveryAddressSelection from '../../components/deliveryAddressSelection.vue';
import paymentSelection from '../../components/paymentSelection.vue';
import loadingButton from '../../components/loadingButton.vue';
import modal from '../../components/modal.vue';
import confirmOrderModal from '../../components/confirmOrderModal.vue';
import spinner from '../../components/spinner.vue';
import errorBox from '../../components/errorBox.vue';
import accountInfoFormModal from '../../components/accountInfoFormModal.vue';
import { instance } from '../../utils/ajax';
import { createForm } from '../../utils/formUtil';
import askLoginRegisterModal from '../../components/askLoginRegisterModal.vue';

async function getMerchantSalesDetailByToken(token) {
    try {
        const result = await instance.get('/api/merchantSales/getDetail', {
            params: { token }
        });
        return result.data; //return result.data.data.merchantSales;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error get merchant sales by token');
    }
    return undefined;
}

async function createMerchantSalesOrder({
    mchtSaleAccsTknNum,
    prodOrdQty,
    cmpySiteKey,
    pymtSrvcPrvdId,
    pymtMthdId
}) {
    try {
        let result = await instance.post('/api/merchantSales/createOrder', {
            mchtSaleAccsTknNum,
            prodOrdQty,
            cmpySiteKey,
            pymtSrvcPrvdId,
            pymtMthdId
        });
        return result.data;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error salesLink checkout');
    }
    return {
        success: false,
        data: {
            errors: {
                server: 'error.connect'
            }
        }
    };
}

export default {
    components: {
        productInfo,
        quantityInput,
        shoppingCartSvg,
        productTable,
        deliveryAddressSelection,
        paymentSelection,
        loadingButton,
        modal,
        confirmOrderModal,
        spinner,
        errorBox,
        accountInfoFormModal,
        askLoginRegisterModal
    },
    filters: { price },
    data() {
        const u = this.$store.state.user;

        return {
            loading: false,
            showAskLoginRegisterModal: false,
            showConfirmPage: false,
            showInfoModal: false,
            transitionName: '',
            status: undefined,
            errors: {},
            showDeliverySiteOrPaymentNotSelectedModal: false,
            orderStatusModalMsg: undefined,
            dlvryCmpySiteKey: u?.cmpySiteKey,
            paymentOption: undefined,
            showConfirmOrder: false,
            product: undefined,
            productLinks: []
        };
    },
    computed: {
        user() {
            return this.$store.state.user;
        },
        showDlvryCmpySiteAddr() {
            return this.user?.custTypeCode === 'PUB';
        },
        productActualRemainingQty() {
            return this.$store.getters.productActualRemainingQty(this.product);
        },
        isLessThanOrigPrcAmt() {
            return this.$store.getters.isLessThanOrigPrcAmt(this.product);
        },
        maxCustOrdQty() {
            return this.$store.getters.getProductMaxOrdQtyPerPerson(
                this.product
            );
        },
        totCustOrdQty() {
            return this.product.totCustOrdQty;
        },
        maxQty() {
            const maxQty = this.maxCustOrdQty
                ? Math.min(
                      this.maxCustOrdQty - this.totCustOrdQty,
                      this.productActualRemainingQty
                  )
                : this.productActualRemainingQty;
            return Math.max(maxQty, 0);
        },
        productTotalPrice() {
            return this.product.prodOrdQty * this.product.prodUnitPrcAmt;
        },
        cpnDsctAmt() {
            // Currently not support coupon
            return 0;
        },
        adhocDsctAmt() {
            // Currently not support adhoc discount
            return 0;
        },
        crdtPntDsctAmt() {
            // Currently not support spending credit point
            return 0;
        },
        crdtPntEarn() {
            // Currently not support earning credit point
            return 0;
        },
        cartCanProcess() {
            return this.product.prodOrdQty <= this.maxQty;
        },
        isFriendCustomer() {
            return this.$store.getters.isFriendCustomer;
        },
        needPayment() {
            return (
                this.productTotalPrice -
                    this.cpnDsctAmt -
                    this.adhocDsctAmt -
                    this.crdtPntDsctAmt >
                0
            );
        }
    },
    async beforeMount() {
        this.loading = true;
        const result = await getMerchantSalesDetailByToken(
            this.$route.params.token
        );
        const merchantSales = result?.data?.merchantSales;
        const productLinks = result?.data?.productLinks;
        if (!result?.data.merchantSales) {
            this.$router.push('/');
            return;
        }
        this.product = {
            ...merchantSales,
            prodOrdQty: 1
        };
        this.productLinks = productLinks || [];
        this.loading = false;
    },
    methods: {
        switchToConfirmPage() {
            if (!this.user) {
                this.showAskLoginRegisterModal = true;
                return;
            }

            this.transitionName = 'slide-left';
            this.showConfirmPage = true;
            this.paymentOption = undefined;
            this.scrollToTop();
        },
        switchToProductInfoPage() {
            this.transitionName = 'slide-right';
            this.showConfirmPage = false;
            this.paymentOption = undefined;
            this.scrollToTop();
        },
        scrollToTop() {
            window.scrollTo(0, 0);
        },
        scrollToDlvrySiteAndPaymentSelection() {
            this.$refs.dlvrySiteAndPaymentSelection.scrollIntoView();
        },
        async requestConfirmOrder() {
            if (
                (this.needPayment && !this.paymentOption) ||
                !this.dlvryCmpySiteKey
            ) {
                this.scrollToDlvrySiteAndPaymentSelection();
                this.showDeliverySiteOrPaymentNotSelectedModal = true;
                return;
            }

            const isInfoCompleted = await this.$store.dispatch(
                'getIsAccountInfoCompleted'
            );
            if (!isInfoCompleted) {
                this.status = undefined;
                this.showInfoModal = true;
                return;
            }

            this.showConfirmOrder = true;
        },
        async confirmOrder() {
            this.showConfirmOrder = false;
            this.orderStatusModalMsg = this.$t('order.creating');
            this.status = 'loading';

            this.errors = {};

            const result = await createMerchantSalesOrder({
                mchtSaleAccsTknNum: this.product.mchtSaleAccsTknNum,
                prodOrdQty: this.product.prodOrdQty,
                cmpySiteKey: this.dlvryCmpySiteKey,
                pymtSrvcPrvdId: this.paymentOption?.pymtSrvcPrvdId,
                pymtMthdId: this.paymentOption?.pymtMthdId
            });

            this.status = undefined;

            if (result.success) {
                this.$store.commit('setUser', result.data.user);
                const salesOrder = result.data.salesOrder;
                const paymentType = result.data.paymentType;
                const paymentDetails = result.data.paymentDetails;
                if (paymentType === 'HSBC') {
                    this.$store.commit('setFpsData', {
                        fpsQrCode: paymentDetails.qrCode,
                        fpsCurrentOrdNo: salesOrder.saleOrdTxnNum,
                        fpsCurrentOrdKey: salesOrder.id,
                        fpsPymtAmt: salesOrder.pymtTotAmt
                    });
                    this.$router.push({ name: 'fps' });
                } else if (paymentType === 'MPAY') {
                    let form = createForm(
                        'POST',
                        paymentDetails.paymentUrl,
                        paymentDetails.formBody
                    );
                    form.submit();
                } else {
                    // No need payment or Paid by cash
                    // Redirect to order history page
                    this.redirectToOrderHistory(salesOrder.saleOrdTxnNum);
                }
            } else {
                this.orderStatusModalMsg = undefined;
                this.errors = result.data.errors;
                if (result.data.merchantSales) {
                    this.product.rmnProdQty =
                        result.data.merchantSales.rmnProdQty;
                    this.product.stckAvailQty =
                        result.data.merchantSales.stckAvailQty;
                    this.product.totProdSoldQty =
                        result.data.merchantSales.totProdSoldQty;
                }
            }
        },
        redirectToOrderHistory(saleOrdTxnNum) {
            this.$router.push({
                name: 'order',
                params: {
                    orderNo: saleOrdTxnNum
                },
                query: {
                    success: 'true'
                }
            });
        },
        async infoSaveSuccess() {
            this.showInfoModal = false;
            await this.requestConfirmOrder();
        }
    }
};
</script>
